import {ReactElement, useEffect, useState} from "react";
import {Navigation} from "components/UIComponents/Navigation";
import {SubTitle} from "components/UIComponents/SubTitle";
import {FormattedMessage} from "react-intl";
import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import {PrimaryButton} from "components/UIComponents/PrimaryButton";
import {ImageGrid} from "components/UIComponents/ImageGrid";
import {ImageGroup} from "./imageCollections";
import {ImagePickerImagesArg, ImagePickerProps, toPromise} from "./ImagePicker";
import {Spinner} from "components/UIComponents/Spinner";
import {isEmpty} from "ramda";
import {SecondaryButton} from "components/UIComponents/SecondaryButton";
import {IconLabel} from "components/UIComponents/IconLabel";
import {ReactComponent as ImageIcon} from "components/Icons/image.svg";
import {ImagePickerUploadModal} from "./ImagePickerUploadModal";


export interface ImagePickerPopupProps {
    images: ImagePickerImagesArg
    onPick: (url: string) => void
    onClose: () => void
    upload: ImagePickerProps['upload']
}

export function ImagePickerPopup(props: ImagePickerPopupProps): ReactElement {
    const [chosenImage, setChosenImage] = useState<string | null>(null);
    const [imageGroups, setImageGroups] = useState<ImageGroup[] | null>(null);
    const [imageUploaderOpen, setImageUploaderOpen] = useState(false);

    const imagesArg = props.images;
    useEffect(() => {
        if (imageGroups !== null) {
            return;
        }
        toPromise(imagesArg).then(setImageGroups);
    }, [imagesArg, imageGroups]);

    const onPick = () => {
        if (chosenImage == null) {
            return;
        }
        props.onPick(chosenImage);
    }

    return (
        <div className="w-full h-full flex flex-col items-stretch bg-white">

            {(imageUploaderOpen && props.upload != null) && (
                <ImagePickerUploadModal
                    open={imageUploaderOpen}
                    onClose={() => setImageUploaderOpen(false)}
                    upload={{
                        ...props.upload,
                        onCancel: () => setImageUploaderOpen(false),
                        onSuccess: () => {
                            setImageUploaderOpen(false);
                            setImageGroups(null);
                        }
                    }}
                />
            )}

            <div className="flex-none border-b border-gray-2">
                <Navigation
                    left={<>
                        <SubTitle><FormattedMessage id="editor.image.picker.title"/></SubTitle>
                    </>}
                    right={<>
                        {props.upload != null && (
                            <SecondaryButton onClick={() => setImageUploaderOpen(true)}>
                                <IconLabel icon={<ImageIcon className="h-4 w-4"/>}>
                                    <FormattedMessage id="editor.image.upload"/>
                                </IconLabel>
                            </SecondaryButton>
                        )}
                        <PrimaryButton
                            disabled={chosenImage == null}
                            onClick={onPick}>
                            <FormattedMessage id="editor.image.picker.chose"/>
                        </PrimaryButton>
                        <span/>
                        <button onClick={props.onClose}><CloseIcon/></button>
                    </>}
                />
            </div>

            <div className="flex-auto overflow-y-auto pl-11">
                {/* double div because else we have the scroll bar on 2/3 width */}
                <div className="w-2/3">
                    {imageGroups != null ? (
                        imageGroups
                            .filter(g => !isEmpty(g.images))
                            .map(imageGroup => (
                                <div key={imageGroup.name} className="pt-16 px-12">
                                    <div className="pb-10"><SubTitle>{imageGroup.name}</SubTitle></div>
                                    <ImageGrid
                                        images={imageGroup.images}
                                        selected={chosenImage}
                                        onSelect={setChosenImage}
                                    />
                                </div>
                            ))
                    ) : (
                        <div className="w-screen h-screen flex justify-center items-center">
                            <Spinner className="w-8 h-8"/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}